import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowLeft,
  CheckCircle2,
  ShieldCheck,
  Loader2,
} from "lucide-react";
import NetworkCanvas from "@/components/NetworkCanvas";

const TYPES = {
  diagnostic: {
    badge: "Sentinel Diagnostic",
    title: "Show us your data. We'll show you the blind spots.",
    sub: "A written diagnostic of your blind spots, a measured trust-score gap and the potential for AML & fraud false-positive reduction — in two weeks.",
    accent: "#7c3aed",
    chips: ["AML+Fraud", "Missed risk", "Two-week return"],
  },
  workshop: {
    badge: "Workshop with your team",
    title: "Map your stack and your regulatory exposure — together.",
    sub: "A one-day workshop with your compliance, fraud and engineering leads. We map your typologies, your regulatory exposure and where Sentinel sits on top.",
    accent: "#0047ff",
    chips: ["1-day", "On-site or remote", "Compliance + Eng"],
  },
  proposal: {
    badge: "Tailored proposal",
    title: "Already know what you need? Skip ahead.",
    sub: "Tell us your model, stack and footprint. We come back with a tailored proposal — scope, integration plan and price.",
    accent: "#f59e0b",
    chips: ["Tailored scope", "Integration plan", "Pricing"],
  },
  subscribe: {
    badge: "The Lexifide.ai brief",
    title: "One short note when something actually moves.",
    sub: "AMLA, EU AI Act and DORA — distilled. No more than once a fortnight, and only when there is something worth saying.",
    accent: "#10b981",
    chips: ["Fortnightly max", "Cancel anytime", "No marketing waffle"],
  },
  general: {
    badge: "Talk to the team",
    title: "Tell us what you'd like to discuss.",
    sub: "Whatever stage you are at — curiosity, RFP draft, vendor review — start here. We respond within one business day.",
    accent: "#00b8c9",
    chips: ["1-day reply", "Confidential", "EU-resident"],
  },
};

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const GetStarted = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const typeKey = (query.get("type") || "diagnostic").toLowerCase();
  const source = query.get("source") || "";
  const type = TYPES[typeKey] || TYPES.diagnostic;

  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    document.title = `${type.badge} — Lexifide.ai`;
  }, [type.badge]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === "submitting") return;
    setStatus("submitting");
    setErrorMsg("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: typeKey,
          name: form.name,
          email: form.email,
          company: form.company || null,
          role: form.role || null,
          message: form.message || null,
          source: source || null,
        }),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `Request failed: ${res.status}`);
      }
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.message || "Something went wrong. Please email hello@lexifide.ai.");
    }
  };

  return (
    <div data-testid="get-started-page">
      {/* Hero strip */}
      <section className="relative dark-section overflow-hidden pt-[120px] lg:pt-[140px] pb-16">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-dot-grid opacity-40" />
          <NetworkCanvas />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(9,10,15,0.4) 0%, rgba(9,10,15,0.85) 70%, #090a0f 100%)",
            }}
          />
        </div>
        <div className="relative max-w-[1100px] mx-auto px-6 lg:px-10">
          <button
            onClick={() => navigate(-1)}
            data-testid="get-started-back"
            className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.22em] uppercase text-white/55 hover:text-white mb-10"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>

          <span
            className="kicker"
            style={{ color: type.accent, borderColor: `${type.accent}55` }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ backgroundColor: type.accent }}
            />
            {type.badge}
          </span>
          <h1
            data-testid="get-started-title"
            className="mt-5 font-display font-bold text-white tracking-tight text-[36px] sm:text-[48px] lg:text-[60px] leading-[1.04] max-w-[20ch]"
          >
            {type.title}
          </h1>
          <p className="mt-5 text-white/65 text-[16px] sm:text-[18px] leading-relaxed max-w-[62ch]">
            {type.sub}
          </p>

          <div className="mt-6 flex flex-wrap gap-2">
            {type.chips.map((c) => (
              <span
                key={c}
                className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/70 border border-white/15 px-2.5 py-1.5"
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Form / Success body */}
      <section className="light-section py-20 lg:py-28">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Form */}
            <div className="lg:col-span-7">
              {status === "success" ? (
                <div
                  data-testid="get-started-success"
                  className="border border-[#e4e4e7] bg-white p-10 lg:p-12 relative"
                >
                  <span
                    className="absolute top-0 left-0 right-0 h-[3px]"
                    style={{ backgroundColor: type.accent }}
                  />
                  <span
                    className="inline-flex items-center justify-center w-12 h-12 mb-5"
                    style={{ backgroundColor: `${type.accent}1a` }}
                  >
                    <CheckCircle2
                      className="w-6 h-6"
                      style={{ color: type.accent }}
                      strokeWidth={2}
                    />
                  </span>
                  <h2 className="font-display font-bold text-[#090a0f] text-3xl lg:text-[40px] tracking-tight leading-tight">
                    Thanks — we&rsquo;ll be in touch.
                  </h2>
                  <p className="mt-4 text-[#52525b] text-[15.5px] leading-relaxed max-w-[58ch]">
                    Your request has been captured. A member of the Lexifide.ai
                    team will reach out within one business day, Mon&ndash;Fri,
                    CET, to schedule next steps.
                  </p>
                  <div className="mt-8 flex flex-col sm:flex-row gap-3">
                    <Link to="/" data-testid="get-started-back-home" className="btn-primary-light">
                      Back to home <ArrowUpRight className="w-4 h-4" />
                    </Link>
                    <Link to="/product" data-testid="get-started-explore-product" className="btn-ghost-light">
                      Explore the product
                    </Link>
                  </div>
                </div>
              ) : (
                <form onSubmit={onSubmit} data-testid="get-started-form" className="border border-[#e4e4e7] bg-white p-8 lg:p-10 relative">
                  <span
                    className="absolute top-0 left-0 right-0 h-[3px]"
                    style={{ backgroundColor: type.accent }}
                  />
                  <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#52525b] mb-6">
                    A few details &mdash; that&rsquo;s it.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <Field
                      id="name"
                      label="Full name"
                      required
                      value={form.name}
                      onChange={onChange}
                      autoComplete="name"
                      testId="field-name"
                    />
                    <Field
                      id="email"
                      label="Work email"
                      type="email"
                      required
                      value={form.email}
                      onChange={onChange}
                      autoComplete="email"
                      testId="field-email"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <Field
                      id="company"
                      label="Bank / Company"
                      value={form.company}
                      onChange={onChange}
                      autoComplete="organization"
                      testId="field-company"
                    />
                    <Field
                      id="role"
                      label="Role"
                      value={form.role}
                      onChange={onChange}
                      placeholder="e.g. Head of AML, MLRO, CISO"
                      autoComplete="organization-title"
                      testId="field-role"
                    />
                  </div>

                  <div className="mb-6">
                    <label htmlFor="message" className="block font-mono text-[10px] tracking-[0.22em] uppercase text-[#52525b] mb-2">
                      What would you like to discuss?
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      rows={4}
                      placeholder={
                        typeKey === "subscribe"
                          ? "Any topics you'd particularly like us to cover? (optional)"
                          : typeKey === "proposal"
                          ? "Stack, footprint, anything we should know."
                          : "Tenant model, vendor stack, the most painful alert backlog — anything that helps us prepare."
                      }
                      data-testid="field-message"
                      className="block w-full bg-white border border-[#e4e4e7] focus:border-[#090a0f] focus:outline-none px-4 py-3 text-[15px] text-[#090a0f] placeholder:text-[#a1a1aa] transition-colors"
                    />
                  </div>

                  {status === "error" && (
                    <p data-testid="get-started-error" className="mb-4 text-[14px] text-[#f43f5e]">
                      {errorMsg}
                    </p>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                    <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#a1a1aa]">
                      Confidential &middot; EU-resident processing
                    </p>
                    <button
                      type="submit"
                      data-testid="get-started-submit"
                      disabled={status === "submitting"}
                      className="btn-primary-light disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {status === "submitting" ? (
                        <>
                          Sending <Loader2 className="w-4 h-4 animate-spin" />
                        </>
                      ) : (
                        <>
                          {typeKey === "subscribe" ? "Subscribe" : "Send request"} <ArrowUpRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Side trust panel */}
            <aside className="lg:col-span-5">
              <div className="border border-[#e4e4e7] bg-[#fafafa] p-8 lg:p-10">
                <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#52525b] mb-4">
                  What happens next
                </p>
                <ul className="space-y-5">
                  {[
                    {
                      t: "We reply within 1 business day",
                      d: "Mon–Fri, CET. From a real human at Lexifide.ai.",
                    },
                    {
                      t: "Short, confidential intro call",
                      d: "20 minutes. NDA-friendly.",
                    },
                    {
                      t: "Then — whatever fits",
                      d: "Diagnostic, workshop, proposal. No pressure, no template.",
                    },
                  ].map((s, i) => (
                    <li key={s.t} className="flex items-start gap-4">
                      <span
                        className="mt-0.5 inline-flex items-center justify-center w-6 h-6 font-mono text-[10px] tracking-tight font-semibold"
                        style={{
                          color: type.accent,
                          backgroundColor: `${type.accent}1f`,
                        }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div>
                        <p className="font-display font-semibold text-[#090a0f] text-[15px]">
                          {s.t}
                        </p>
                        <p className="mt-1 text-[#52525b] text-[13.5px] leading-relaxed">
                          {s.d}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 pt-6 border-t border-[#e4e4e7] flex items-start gap-3">
                  <ShieldCheck className="w-4 h-4 text-[#10b981] mt-0.5" strokeWidth={2} />
                  <p className="text-[#52525b] text-[13px] leading-relaxed">
                    Your details are stored in our EU-resident database and used
                    only to respond to your request. We won&rsquo;t share, sell
                    or rent them. Ever.
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

const Field = ({ id, label, type = "text", required, value, onChange, placeholder, autoComplete, testId }) => (
  <div>
    <label htmlFor={id} className="block font-mono text-[10px] tracking-[0.22em] uppercase text-[#52525b] mb-2">
      {label} {required && <span className="text-[#f43f5e]">*</span>}
    </label>
    <input
      id={id}
      name={id}
      type={type}
      required={required}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoComplete={autoComplete}
      data-testid={testId}
      className="block w-full bg-white border border-[#e4e4e7] focus:border-[#090a0f] focus:outline-none px-4 py-3 text-[15px] text-[#090a0f] placeholder:text-[#a1a1aa] transition-colors"
    />
  </div>
);

export default GetStarted;
